/**
 * A revelação ao entrar na tela, do Yard Design System.
 *
 * O `yard.css` traz a metade de CSS: `[data-reveal]` nasce invisível e
 * deslocado, e volta ao lugar quando ganha a classe `.in`. Quem põe a classe,
 * no site de documentação, é o `yard.js`. Aqui é isto.
 *
 * POR QUE UM OBSERVADOR SÓ, NO DOCUMENTO INTEIRO
 *
 *   A alternativa seria um `useEffect` com um observador por componente. Numa
 *   lista de quarenta cartões isso são quarenta observadores, cada um com o seu
 *   custo de registro, para fazer exatamente a mesma coisa. Um observador só,
 *   com um `MutationObserver` avisando quando chega elemento novo, cobre tudo
 *   que a aplicação montar — inclusive o que veio depois, por rota preguiçosa.
 *
 * POR QUE ELE DESISTE DO ELEMENTO DEPOIS DE REVELAR
 *
 *   A revelação é de uma vez só: rolar de volta para cima não deve apagar e
 *   reacender o conteúdo. `unobserve` no primeiro cruzamento também impede o
 *   observador de crescer sem limite numa lista infinita.
 */

const MARCA = 'data-reveal';

let observador: IntersectionObserver | null = null;

/**
 * Quem prefere menos movimento não recebe nem o observador.
 *
 * O CSS já anula a animação nesse caso, mas sem esta saída os elementos
 * ficariam dependendo de uma classe que ninguém poria — e `[data-reveal]`
 * sem `.in` é `opacity:0`. O conteúdo simplesmente não apareceria.
 */
const menosMovimento = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function revelarTudoDeUmaVez() {
  document.querySelectorAll(`[${MARCA}]`).forEach((el) => el.classList.add('in'));
}

/**
 * A rede de segurança: o que está dentro da janela e continua sem `.in`
 * passado um instante entra assim mesmo.
 *
 * O IntersectionObserver não computa nada enquanto a aba está escondida
 * (aberta em segundo plano, ou trocada no meio do carregamento), e em alguns
 * desses casos o cruzamento de um elemento que chegou nesse meio tempo nunca
 * é entregue - o cartão fica em `opacity:0` e a tela parece não ter
 * carregado. Como `[data-reveal]` sem `.in` é invisível, a falha é
 * silenciosa; daí o prazo curto e a checagem ao voltar para a aba.
 */
const PRAZO = 1200;
function revelarSeNaJanela(el: Element) {
  if (el.classList.contains('in')) return;
  const r = el.getBoundingClientRect();
  if (r.top < window.innerHeight && r.bottom > 0 && r.height > 0) {
    el.classList.add('in');
    observador?.unobserve(el);
  }
}
function revelarOQueFicouNaJanela() {
  document.querySelectorAll(`[${MARCA}]:not(.in)`).forEach(revelarSeNaJanela);
}

function olhar(raiz: ParentNode) {
  if (!observador) return;
  raiz.querySelectorAll(`[${MARCA}]:not(.in)`).forEach((el) => {
    observador!.observe(el);
    window.setTimeout(() => revelarSeNaJanela(el), PRAZO);
    // Cascata: cada filho direto de um `data-stagger` entra um degrau depois do
    // anterior. O `--i` é o que o `transition-delay` do CSS multiplica.
    if (el.hasAttribute('data-stagger')) {
      const filhos = [...el.children];
      // Cascata de 60ms é o padrão do sistema; acima de oito itens ela cai para
      // 30ms. Com 60ms, o décimo segundo cartão de uma lista entraria mais de
      // meio segundo depois do primeiro — a essa altura já não se lê como uma
      // lista chegando, e sim como a página travando.
      if (filhos.length > 8) (el as HTMLElement).style.setProperty('--stagger', '30ms');
      filhos.forEach((filho, i) => {
        (filho as HTMLElement).style.setProperty('--i', String(i));
      });
    }
  });
}

/** Liga a revelação. Chamar uma vez, na raiz da aplicação. */
export function ligarRevelacao(): () => void {
  if (typeof window === 'undefined') return () => {};

  if (menosMovimento()) {
    revelarTudoDeUmaVez();
    return () => {};
  }

  observador = new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        // A margem de 12% embaixo existe para a revelação acontecer no campo
        // de atenção de quem rola. Mas o que já está na tela ao abrir a página
        // e cai nessa faixa nunca cruza a margem se a página não tem o que
        // rolar - ficava invisível para sempre, e a tela parecia não ter
        // carregado. O que já está dentro da janela de verdade entra na hora.
        const r = entrada.boundingClientRect;
        const naJanela = r.top < window.innerHeight && r.bottom > 0;
        if (!entrada.isIntersecting && !naJanela) continue;
        entrada.target.classList.add('in');
        observador?.unobserve(entrada.target);
      }
    },
    // A margem negativa embaixo faz o elemento entrar quando já subiu um pouco
    // na tela, e não no instante em que a primeira linha de pixel aparece —
    // senão a animação acontece fora do campo de atenção de quem lê.
    //
    // O atraso vem daí, do `rootMargin`, e o `threshold` fica em 0 de
    // propósito. Com `threshold` maior que zero a fração exigida é da altura do
    // ELEMENTO, não da tela: um bloco muito mais alto que a janela, ou um bloco
    // de altura zero no instante em que é observado (dentro de um acordeão
    // fechado, de uma aba que ainda não abriu), nunca alcança a fração — e como
    // `[data-reveal]` sem `.in` é `opacity:0`, ele fica invisível para sempre.
    // É uma falha silenciosa: não dá erro, o conteúdo simplesmente não está lá.
    { rootMargin: '0px 0px -12% 0px', threshold: 0 },
  );

  olhar(document);

  // Rota nova, lista que cresceu, modal que abriu: o que chegar depois também
  // entra na conta.
  const mutacoes = new MutationObserver((lista) => {
    for (const m of lista) {
      m.addedNodes.forEach((no) => {
        if (no.nodeType !== 1) return;
        const el = no as HTMLElement;
        if (el.hasAttribute?.(MARCA)) {
          observador?.observe(el);
          window.setTimeout(() => revelarSeNaJanela(el), PRAZO);
        }
        olhar(el);
      });
    }
  });
  mutacoes.observe(document.body, { childList: true, subtree: true });

  const aoVoltar = () => { if (!document.hidden) revelarOQueFicouNaJanela(); };
  document.addEventListener('visibilitychange', aoVoltar);
  window.addEventListener('pageshow', aoVoltar);

  // Segunda rede de segurança: a cada rolagem (uma vez por quadro), o que está
  // na janela e continua sem `.in` entra. Foi visto um cartão de superfície
  // da Bio invisível no topo do painel com tudo abaixo dele já revelado -
  // o cruzamento nunca chegou, e o prazo de 1,2s já tinha passado quando o
  // cartão foi trocado pelo React. Rolar (ou o simples passar do tempo, no
  // `setInterval` curto abaixo, que para sozinho quando não sobra nada) é o
  // que garante que nada fica em `opacity:0` para sempre.
  let agendado = 0;
  const aoRolar = () => {
    if (agendado) return;
    agendado = window.requestAnimationFrame(() => { agendado = 0; revelarOQueFicouNaJanela(); });
  };
  window.addEventListener('scroll', aoRolar, { passive: true });
  window.addEventListener('resize', aoRolar, { passive: true });
  const vigia = window.setInterval(revelarOQueFicouNaJanela, 800);

  return () => {
    document.removeEventListener('visibilitychange', aoVoltar);
    window.removeEventListener('pageshow', aoVoltar);
    window.removeEventListener('scroll', aoRolar);
    window.removeEventListener('resize', aoRolar);
    window.clearInterval(vigia);
    mutacoes.disconnect();
    observador?.disconnect();
    observador = null;
  };
}
